// import { ThemeToggle } from '@/components/shared/theme/theme-toggle';
import { Avatar, AvatarFallback, AvatarImage } from '@crowdfpl/ui-web/avatar';
import { Button } from '@crowdfpl/ui-web/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@crowdfpl/ui-web/dropdown-menu';
import { Skeleton } from '@crowdfpl/ui-web/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@crowdfpl/ui-web/tooltip';
import { ChevronDown, BarChart3, Crown, MoreHorizontal, Swords, Trophy, User, Users, LogOut, HelpCircle } from 'lucide-react';
import { useRef, useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { GamificationFeatureGate } from '@/components/gamification/gamification-feature-gate';
import { TierBadgeButton } from '@/components/gamification/tier-badge-button';
import { NotificationCenter } from '@/components/notifications';
import { resolveOnboardingHeaderSlot } from '@/components/shared/layout/onboarding-header-placement';
import { trackUmamiEvent } from '@/helpers/analytics';
import { useAuth } from '@/hooks/auth';
import { useSignOut } from '@/hooks/data/auth';
import { useAppConfig } from '@/hooks/data/config';
import { useIsMobileNav } from '@/hooks/utils/use-mobile';
import { useOnboarding } from '@/stores/onboarding';

interface GlobalHeaderProps {
  onAuthModalOpen: () => void;
}

export function GlobalHeader({ onAuthModalOpen }: GlobalHeaderProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, loading, user } = useAuth();
  const isMobileNav = useIsMobileNav();
  const signOut = useSignOut();
  const { show: showOnboarding } = useOnboarding();
  const { data: appConfig, isPending: configPending, isError: configError } = useAppConfig();
  const configReady = !configPending || configError;
  const gamificationEnabled = appConfig?.features.gamification === true;
  const onboardingSlot = resolveOnboardingHeaderSlot(isAuthenticated, gamificationEnabled, configReady);

  const navItems: { path: string; label: string; icon: typeof Swords }[] = [
    { path: '/duels', label: 'Duels', icon: Swords },
    { path: '/crowd-rating', label: 'Crowd Power', icon: BarChart3 },
    { path: '/captain', label: 'Captain', icon: Crown },
    { path: '/leaderboard', label: 'Leaderboard', icon: Trophy },
    { path: '/leagues', label: 'Leagues', icon: Users },
  ];

  const navRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(navItems.length);

  useEffect(() => {
    if (isMobileNav) return;

    let rafId: number | null = null;
    const gapPx = 4; // space-x-1

    const updateVisible = () => {
      const container = navRef.current;
      const measure = measureRef.current;
      if (!container || !measure) return;

      const available = Math.round(container.getBoundingClientRect().width);
      // Ignore detached/hidden states (width=0) to avoid locking into "More".
      if (available <= 0) return;

      const children = Array.from(measure.children) as HTMLElement[];
      if (children.length < 2) return;

      const moreEl = children[children.length - 1];
      const itemEls = children.slice(0, -1);
      const itemWidths = itemEls.map((el) => el.getBoundingClientRect().width);
      const moreWidth = moreEl.getBoundingClientRect().width;

      const widthFor = (count: number, withMore: boolean): number => {
        let total = 0;
        for (let i = 0; i < count; i += 1) {
          total += itemWidths[i] ?? 0;
        }
        total += Math.max(0, count - 1) * gapPx;
        if (withMore) {
          total += (count > 0 ? gapPx : 0) + moreWidth;
        }
        return total;
      };

      let nextVisibleCount = 1;
      if (widthFor(itemWidths.length, false) <= available) {
        nextVisibleCount = itemWidths.length;
      } else {
        for (let count = itemWidths.length - 1; count >= 1; count -= 1) {
          if (widthFor(count, true) <= available) {
            nextVisibleCount = count;
            break;
          }
        }
      }

      setVisibleCount((prev) => (prev === nextVisibleCount ? prev : nextVisibleCount));
    };

    const scheduleUpdate = () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(updateVisible);
    };

    scheduleUpdate();

    const ro = new ResizeObserver(scheduleUpdate);
    const navEl = navRef.current;
    const measureEl = measureRef.current;
    if (navEl) ro.observe(navEl);
    if (measureEl) ro.observe(measureEl);

    window.addEventListener('resize', scheduleUpdate);

    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      ro.disconnect();
      window.removeEventListener('resize', scheduleUpdate);
    };
  }, [isMobileNav, navItems.length]);

  const isActivePath = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const handleLogout = async () => {
    await signOut.mutateAsync();
    navigate('/');
  };

  const navLinkClass = (active: boolean) =>
    `inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-colors h-9 rounded-md px-3 shrink-0 ${
      active
        ? 'bg-primary text-primary-foreground shadow hover:bg-primary/90'
        : 'hover:bg-accent hover:text-accent-foreground'
    }`;

  // Don't render when compact nav (mobile + tablet) is active
  if (isMobileNav) return null;

  return (
    <header
      className="sticky top-0 z-50 h-16 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border"
      role="banner"
    >
      <div className="container mx-auto grid h-full grid-cols-[1fr_minmax(320px,2fr)_1fr] items-center gap-4 px-4">
        {/* Logo - left column */}
        <div className="flex min-w-0 items-center justify-start">
          <Link
            to="/"
            onClick={(e) => {
              if (window.location.pathname === '/') {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }
            }}
            className="logo flex items-center hover:opacity-80 transition-opacity"
            aria-label="Go to homepage"
          >
            <img src="/icons/crowdfpl-icon.svg" alt="CrowdFPL logo" width="28" height="28" />
            <span className="brand-name text-xl font-bold">CrowdFPL</span>
          </Link>
        </div>

        {/* Navigation - center column (equal columns = nav visually centered) */}
        <div ref={navRef} className="relative flex min-w-0 flex-1 justify-center overflow-hidden px-2">
          {/* Offscreen measure row: real widths for adaptive More (Crowd Power / Leaderboard ≠ 88px) */}
          <div
            ref={measureRef}
            className="pointer-events-none absolute left-0 top-0 flex -translate-x-[200%] items-center space-x-1 opacity-0"
            aria-hidden
          >
            {navItems.map((item) => (
              <span key={item.path} className={navLinkClass(false)}>
                <item.icon className="h-4 w-4" />
                {item.label}
              </span>
            ))}
            <span className="inline-flex h-9 shrink-0 items-center gap-1 rounded-md px-3 text-sm font-medium">
              <MoreHorizontal className="h-4 w-4" />
              More
            </span>
          </div>

          <nav className="flex shrink-0 items-center justify-center space-x-1" role="navigation" aria-label="Main navigation">
          {navItems.slice(0, visibleCount).map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => trackUmamiEvent('nav_global_header_click', { label: item.label, path: item.path })}
              className={navLinkClass(isActivePath(item.path))}
            >
              <item.icon className="h-4 w-4" />
              {item.label}
            </Link>
          ))}
          {visibleCount < navItems.length && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-9 shrink-0 gap-1 hover:bg-accent hover:text-accent-foreground"
                  aria-label="More navigation"
                >
                  <MoreHorizontal className="h-4 w-4" />
                  More
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {navItems.slice(visibleCount).map((item) => (
                  <DropdownMenuItem key={item.path} asChild>
                    <Link
                      to={item.path}
                      className="flex cursor-pointer items-center"
                      onClick={() => trackUmamiEvent('nav_global_header_click', { label: item.label, path: item.path })}
                    >
                      <item.icon className="mr-2 h-4 w-4" />
                      {item.label}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          </nav>
        </div>

        {/* User Menu / Auth - right */}
        <div className="flex min-w-0 items-center justify-end space-x-2">
          {/* <ThemeToggle /> */}
          {onboardingSlot === 'header' && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={showOnboarding}
                    className="h-10 w-10"
                    aria-label="How it works"
                  >
                    <HelpCircle className="h-5 w-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>How it works</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {onboardingSlot === 'pending' && isAuthenticated && (
            <div className="h-11 w-11 shrink-0" aria-hidden />
          )}
          <GamificationFeatureGate>
            <TierBadgeButton />
          </GamificationFeatureGate>
          <NotificationCenter />
          {loading ? (
            <Skeleton className="h-10 w-28 rounded-md" />
          ) : isAuthenticated ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2 h-10" aria-label="User menu">
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={user?.avatar_url || ''} alt={user?.username || 'User avatar'} />
                    <AvatarFallback>{(user?.username?.[0] || 'U').toUpperCase()}</AvatarFallback>
                  </Avatar>
                  <span className="max-w-32 truncate">{user?.username || 'User'}</span>
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem asChild>
                  <Link to={`/profile/${user?.username || 'user'}`} className="flex items-center cursor-pointer">
                    <User className="mr-2 h-4 w-4" />
                    <span>My Profile</span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/duels" className="flex items-center cursor-pointer">
                    <Swords className="mr-2 h-4 w-4" />
                    <span>Duels</span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/leaderboard" className="flex items-center cursor-pointer">
                    <Trophy className="mr-2 h-4 w-4" />
                    <span>Leaderboard</span>
                  </Link>
                </DropdownMenuItem>
                {onboardingSlot === 'menu' && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={showOnboarding}>
                      <HelpCircle className="mr-2 h-4 w-4" />
                      <span>How it works</span>
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout}>
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Sign Out</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button onClick={() => onAuthModalOpen()}>
              Sign In
            </Button>
          )}
        </div>
      </div>
    </header>
  );
}
